import React, { useEffect, useState, useContext } from 'react';
import { useQuery, useMutation } from "react-query";
import { Modal, Card, Spin, Table, Button, Tooltip, Checkbox, notification, Select } from 'antd';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";
import { hasAccess, hasRoles, ROLES } from '../utils/AccessLevels';
import './ConnectionDashboard.css'
import tr from '../i18n';
import { ReloadOutlined, BarsOutlined, SearchOutlined, WifiOutlined } from '@ant-design/icons';
import { formatDate } from '../utils/DateFormatter';

const ConnectionDashboard = () => {
    const [tenantId, setTenantId] = useState('');
    const [loading, setLoading] = useState(true);
    const [pingLoading, setPingLoading] = useState(false);
    const [multiplePingLoading, setMultiplePingLoading] = useState(false);
    const [searchFilters, setSearchFilters] = useState({});
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [deviceToUpdate, setDeviceToUpdate] = useState(null);
    const [fotaEnabled, setFotaEnabled] = useState(false);
    const [motaEnabled, setMotaEnabled] = useState(false);
    const [mgmtEnabled, setMgmtEnabled] = useState(false);
    const [diagEnabled, setDiagEnabled] = useState(false);
    const [edrxEnabled, setEdrxEnabled] = useState(false);
    const [copsEnabled, setCopsEnabled] = useState(false);
    const [connModeEnabled, setConnModeEnabled] = useState(false);
    const [selectedEdrx, setSelectedEdrx] = useState('20');
    const [selectedFota, setSelectedFota] = useState('SPB_2.5.5');
    const [selectedMota, setSelectedMota] = useState('SPB9V_2.4.9');
    const [selectedCops, setSelectedCops] = useState('att');
    const [selectedconnMode, setSelectedconnMode] = useState('nbiot');
    const [modelValue, setModelValue] = useState("LT");
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const { role } = useContext(AppContext);
    const navigate = useNavigate();

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    };

    useEffect(() => {
        try {
            const userSettings = localStorage.getItem("settings");
            const parsed = JSON.parse(userSettings);
            const parsedTenantId = parsed.hostname?.split('.')[0];
            setTenantId(parsedTenantId);
            if (!role) return;
            if (!hasAccess(ROLES.SUPER_ADMIN, role)) {
                navigate('/control-center/gardens');
            }
        } catch (error) {
            console.error(error);
        }
    }, []);

    const { data: devices = [], isLoading, error, refetch } = useQuery(
        ['devices', tenantId, modelValue, searchFilters],
        async () => {
            let url = `${process.env.REACT_APP_PLANNER_API_END_POINT}/device/edrx-states/${tenantId}/${modelValue}`;

            if (Object.keys(searchFilters).length > 0) {
                const params = new URLSearchParams({
                    filter: JSON.stringify(searchFilters)
                });
                url += `?${params.toString()}`;
            }

            const res = await axios.get(url);
            return res.data.data;
        },
        {
            enabled: !!tenantId,
            refetchOnWindowFocus: false,
        }
    );

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        setSearchFilters(prev => ({
            ...prev,
            [dataIndex]: selectedKeys[0]
        }));
        confirm();
    };

    const handleReset = (clearFilters, dataIndex) => {
        clearFilters();
        setSearchFilters(prev => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
    };


    const onHandleUpdate = async () => {
        await handleFirmwareUpdate.mutateAsync()
    }

    const handleFirmwareUpdate = useMutation(() => {
        return axios.post(
            `${process.env.REACT_APP_PLANNER_API_END_POINT}/device/publish-update`,
            {
                deviceSerial: deviceToUpdate.deviceSerial,
                fota: fotaEnabled ? selectedFota : null,
                mota: motaEnabled ? selectedMota : null,
                mgmt: mgmtEnabled,
                diag: diagEnabled,
                edrx: edrxEnabled ? selectedEdrx : null,
                cops: copsEnabled ? selectedCops : null,
                connmode: connModeEnabled ? selectedconnMode : null
            }
        )
    }, {
        onSuccess: (data, variables) => {
            setDeviceToUpdate(null);
            setShowOptionsModal(false);
            setFotaEnabled(false);
            setMotaEnabled(false);
            setMgmtEnabled(false);
            setEdrxEnabled(false);
            setDiagEnabled(false);
            setConnModeEnabled(false);
            setCopsEnabled(false);
            setSelectedEdrx('20');
            setSelectedFota('SPB_2.5.5');
            setSelectedMota('SPB9V_2.4.9');
            setSelectedCops('att');
            setSelectedconnMode('nbiot');
            notification.success({
                message: "İşlem Başarılı",
                description: ""
            })
            refetch();
        },
        onError: (error, variables) => {
            setDeviceToUpdate(null);
            setShowOptionsModal(false);
            setFotaEnabled(false);
            setMotaEnabled(false);
            setMgmtEnabled(false);
            setEdrxEnabled(false);
            setDiagEnabled(false);
            setConnModeEnabled(false);
            setCopsEnabled(false);
            setSelectedEdrx('20');
            setSelectedFota('SPB_2.5.5');
            setSelectedMota('SPB9V_2.4.9');
            setSelectedCops('att');
            setSelectedconnMode('nbiot');
            notification.error({
                message: `Hata: ${error}`,
                description: ""
            })
            refetch();
        }
    });

    const onHandlePing = async (device = deviceToUpdate, lite = false) => {
        if (!device) return;
        await pingDevice.mutateAsync({
            deviceSerial: device.deviceSerial,
            ip: device.ip,
            lite: lite
        });
    }

    const pingDevice = useMutation(({ deviceSerial, ip, lite = false }) => {
        return axios.post(
            `${process.env.REACT_APP_PLANNER_API_END_POINT}/device/ping`,
            {
                deviceSerial: deviceSerial,
                ip: ip,
                lite: lite
            }
        )
    }, {
        retry: false,
        onMutate: () => {
            setPingLoading(true);
        },
        onSuccess: (data, variables) => {
            setPingLoading(false);
            notification.success({
                message: "Ping gönderildi",
                description: ""
            })
        },
        onError: (error, variables) => {
            setPingLoading(false);
            const errorMessage = error.response?.data?.error || "Ping işleminde hata";
            notification.error({
                message: `Hata: ${errorMessage}`,
                description: ""
            })
        }
    });

    const handleMultiplePing = async () => {
        const isOk = await new Promise((resolve) => {
            Modal.confirm({
                title: tr('Ping all devices?'),
                content: tr('Are you sure you want to ping all devices?'),
                okText: tr('Confirm'),
                cancelText: tr('Cancel'),
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
            });
        });

        if (isOk) {
            await pingAllTenantDevices.mutateAsync();
        }
    }

    const pingAllTenantDevices = useMutation(() => {
        return axios.post(
            `${process.env.REACT_APP_PLANNER_API_END_POINT}/device/ping-all`,
            {
                tenantId
            }
        )
    }, {
        onMutate: () => {
            setMultiplePingLoading(true);
        },
        onSuccess: (data, variables) => {
            setMultiplePingLoading(false);
            notification.success({
                message: tr('Ping sent to all devices'),
                description: ""
            })
        },
        onError: (error, variables) => {
            setMultiplePingLoading(false);
            const errorMessage = error.response?.data?.error || "Ping all devices failed";
            notification.error({
                message: `Hata: ${errorMessage}`,
                description: ""
            })
        }
    });

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <input
                    placeholder={tr('Search')}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Button
                    type="primary"
                    onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    size="small"
                    style={{ width: 90, marginRight: 8 }}
                >
                    {tr('Search')}
                </Button>
                <Button
                    onClick={() => handleReset(clearFilters, dataIndex)}
                    size="small"
                    style={{ width: 90 }}
                >
                    {tr('Reset')}
                </Button>
            </div>
        ),
        filterIcon: filtered => (
            <span style={{ color: filtered ? '#1890ff' : undefined }}>
                <SearchOutlined />
            </span>
        ),
        onFilterDropdownVisibleChange: visible => {
            if (visible) {
                // focus logic if needed
            }
        },
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
    });

    const renderDateTwoLines = (text) => {
        if (!text) return "";

        const [time, date] = text.split(" ");

        return (
            <div style={{ lineHeight: '1.2' }}>
                <div style={{ whiteSpace: 'nowrap' }}>{time}</div>
                <div style={{ whiteSpace: 'nowrap' }}>{date}</div>
            </div>
        );
    };

    const createColumn = (
        title,
        dataIndex,
        isDate = false,
        parseDate = false,
        customRender,
        width
    ) => ({
        title,
        dataIndex,
        key: dataIndex,
        width,
        render: (text) => {
            if (customRender) return customRender(text);

            return parseDate
                ? formatDate(text)
                : (text || "");
        },
        sorter: (a, b) =>
            isDate
                ? new Date(a[dataIndex] || 0) - new Date(b[dataIndex] || 0)
                : (a[dataIndex] || '').toString().localeCompare((b[dataIndex] || '').toString()),
        ...getColumnSearchProps(dataIndex),
    });

    const baseColumns = [
        createColumn('deviceSerial', 'deviceSerial'),
        createColumn('sid', 'sid'),
        createColumn('version', 'version'),
        {
            ...createColumn('alive', 'alive'),
            render: (status) => (
                <span style={{ color: status === 'alive' ? 'green' : 'red' }}>
                    {status || ''}
                </span>
            ),
        },
    ];

    const simProColumns = [
        createColumn('mode', 'mode'),
        createColumn('time', 'time', true, false, renderDateTwoLines, 120),
        createColumn('ip', 'ip'),
        createColumn('edrx', 'edrx'),
        createColumn('apn', 'apn'),
        createColumn('rebootTime', 'rebootTime', true, false, renderDateTwoLines),
        createColumn('iccid', 'iccid'),
        createColumn('cell', 'cell'),
        createColumn('battery', 'battery'),
        createColumn('signal', 'signal'),
        createColumn('network', 'network'),
        createColumn('radioTech', 'radioTech'),
        createColumn('Ping Tarihi', 'pingSentAt', true, true),
        createColumn('Yanıt Verme Tarihi', 'pongAt', true, true),
        createColumn('Yanıt Verme Süresi', 'timeToPong'),
        createColumn('Uyanma Tarihi', 'awakeAt', true, true),
        createColumn('Uyanma Süresi', 'timeToWake'),
    ];

    const farmerColumns = [
        createColumn('mode', 'mode'),
        createColumn('time', 'time', true)
    ];

    const gardenerColumns = [
        createColumn('bootrtc', 'bootrtc'),
        createColumn('badrtc', 'badrtc')
    ]

    const actionsColumn = {
        title: 'İşlemler',
        key: 'actions',
        render: (_, record) => (
            <div className='flex justify-center'>
                <Button
                    type="secondary"
                    icon={<BarsOutlined />}
                    onClick={() => {
                        setDeviceToUpdate(record);
                        setShowOptionsModal(true);
                    }}
                />
            </div>
        ),
    };

    const pingColumn = {
        title: 'Ping',
        key: 'ping',
        render: (_, record) => (
            <div className='flex justify-center'>
                <Button
                    type="secondary"
                    icon={<WifiOutlined />}
                    onClick={() => {
                        onHandlePing(record, true);
                    }}
                />
            </div>
        ),
    };

    const columns = [
        ...(modelValue === 'LT' ? [actionsColumn, pingColumn] : []),
        ...baseColumns,
        ...(modelValue === 'ELS' ? farmerColumns : []),
        ...(modelValue === 'LT' ? simProColumns : []),
        ...(modelValue === 'EL' ? gardenerColumns : []),
    ];

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: '20px' }}>
            {hasAccess(ROLES.SUPER_ADMIN, role) && isLoading ? (
                <Spin tip="Loading device data..." size="large" />
            ) : error ? (
                <div style={{ color: 'red' }}>Failed to load devices.</div>
            ) : (
                <>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'start',
                        marginBottom: '10px',
                        gap: '8px'
                    }}>
                        <Select
                            style={{ width: 180, marginRight: 8 }}
                            placeholder="Model Seçiniz"
                            value={modelValue}
                            onChange={(value) => {
                                setModelValue(value);
                                setSearchFilters({});
                            }}
                            options={[
                                { value: 'LT', label: 'SimPro' },
                                { value: 'ELS', label: 'Farmer' },
                                { value: 'EL', label: 'Gardener' },
                            ]}
                        />
                        <Button onClick={() => refetch()} type="primary" icon={<ReloadOutlined />} />
                        {modelValue === 'LT' && (
                            <Button
                                type="primary"
                                onClick={() => {
                                    handleMultiplePing();
                                }}
                                size="medium"
                                style={{ marginLeft: 8 }}
                            >
                                Tüm Cihazları Uyandır
                            </Button>
                        )}
                        {modelValue !== 'LT' && selectedRowKeys.length > 0 && (
                            <Button
                                type="primary"
                                style={{ marginLeft: 8 }}
                                onClick={() => {
                                    Modal.confirm({
                                        title: modelValue === "EL" ? "Seçilen cihazlara OTA=SM_2.1.2 göndermek istediğinize emin misiniz?" : 'Seçilen cihazları FRM_2.4.9 versiyonuna güncellemek istediğinize emin misiniz?',
                                        content: (
                                            <div>
                                                <p>Güncellenecek cihazlar:</p>
                                                <ul>
                                                    {selectedRowKeys.map(key => (
                                                        <li key={key}>{key}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ),
                                        okText: 'Evet, Güncelle',
                                        cancelText: 'İptal',
                                        onOk: async () => {
                                            try {
                                                await axios.post(`${process.env.REACT_APP_PLANNER_API_END_POINT}/device/batch-update`, {
                                                    deviceSerials: selectedRowKeys,
                                                    model: modelValue
                                                });
                                                notification.success({
                                                    message: 'Güncelleme başarıyla gönderildi.',
                                                });
                                                setSelectedRowKeys([]);
                                                refetch();
                                            } catch (error) {
                                                notification.error({
                                                    message: 'Güncelleme başarısız oldu.',
                                                    description: error.message,
                                                });
                                            }
                                        },
                                    });
                                }}
                            >
                                {tr('Update')}
                            </Button>
                        )}
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <Table
                            dataSource={devices}
                            columns={columns}
                            rowKey={(record, index) => record.deviceSerial || index}
                            pagination={false}
                            bordered
                            rowSelection={modelValue !== 'LT' ? rowSelection : null}
                        />
                    </div>
                </>
            )}

            {<Modal
                width="22%"
                bodyStyle={{ height: 'auto', overflowY: 'auto' }}
                title={tr('Device Operations')}
                visible={showOptionsModal}
                onOk={() => {
                    if (fotaEnabled || motaEnabled || mgmtEnabled || edrxEnabled || diagEnabled || copsEnabled || connModeEnabled) {
                        Modal.confirm({
                            title: tr('Confirm Submission'),
                            content: (
                                <div className="flex flex-col">
                                    <span>{`Cihaz Seri No: ${deviceToUpdate.deviceSerial}`}</span>
                                    {fotaEnabled && <span>
                                        FOTA={selectedFota}
                                    </span>}
                                    {motaEnabled && <span>
                                        MOTA={selectedMota}
                                    </span>}
                                    {mgmtEnabled && <span>
                                        Mgmt=normal
                                    </span>}
                                    {edrxEnabled && selectedEdrx &&
                                        <span>{`Edrx: ${selectedEdrx}`}</span>
                                    }
                                    {copsEnabled && <span>
                                        cops={selectedCops}
                                    </span>}
                                    {connModeEnabled && <span>
                                        connmode={selectedconnMode}
                                    </span>}
                                </div>
                            ),
                            okText: tr('Confirm'),
                            cancelText: tr('Cancel'),
                            onOk: () => {
                                onHandleUpdate(); // only submit if user confirms
                            }
                        });
                    }
                    else {
                        notification.error({
                            message: `Lütfen işlem seçiniz`,
                            description: ""
                        })
                    }
                }}
                okText={tr('Submit')}
                cancelButtonProps={{ style: { display: "none" } }}
                onCancel={() => {
                    setShowOptionsModal(false);
                    setDeviceToUpdate(null);
                }}>
                <div>
                    {deviceToUpdate && (
                        <div className='flex flex-col'>
                            <div className="text-base font-semibold text-gray-800 mb-3">
                                Cihaz Seri No:
                                <span className="ml-2 px-2 py-1 bg-gray-100 rounded-md font-mono text-black text-lg font-bold">
                                    {deviceToUpdate.deviceSerial}
                                </span>
                            </div>
                            <div className='flex flex-row mb-2 gap-3'>
                                <Button
                                    type="default"
                                    onClick={() => {
                                        onHandlePing(deviceToUpdate);
                                    }}
                                    size="small"
                                >
                                    Ping Gönder
                                </Button>
                                <Spin spinning={pingLoading}></Spin>
                            </div>

                            <div className='flex flex-row border rounded rounded-md p-3 justify-between'>
                                <div className='flex flex-col'>
                                    <div className='grid grid-cols-2 items-center gap-3 mb-3'>
                                        <div className='flex gap-2 mt-2'>
                                            <label className='m-0 p-0'>
                                                <Checkbox
                                                    checked={fotaEnabled}
                                                    onChange={(e) => setFotaEnabled(e.target.checked)}
                                                >
                                                    FOTA
                                                </Checkbox>
                                            </label>
                                        </div>
                                        <select
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            value={selectedFota}
                                            onChange={(e) => setSelectedFota(e.target.value)}
                                            disabled={!fotaEnabled}
                                        >
                                            <option value="SPB_2.5.5">SPB_2.5.5</option>
                                            <option value="SPB9V_2.5.5">SPB9V_2.5.5</option>
                                            <option value="SPB9V_2.4.9">SPB9V_2.4.9</option>
                                            <option value="SPB9V_US_2.5.5">◉ SPB9V_US_2.5.5</option>
                                            <option value="SPBUS_2.1.8">◉ SPBUS_2.1.8</option>
                                            <option value="SPBUS_2.1.2">◉ SPBUS_2.1.2</option>
                                            <option value="SPBUS_2.1.0">◉ SPBUS_2.1.0</option>
                                            <option value="SPBUS_2.0.4">◉ SPBUS_2.0.4</option>
                                            <option value="SPBUS_2.0.0">◉ SPBUS_2.0.0</option>
                                            <option value="SPBUS_1.9.5">◉ SPBUS_1.9.5</option>
                                            <option value="SPB_2.3.2">SPB_2.3.2</option>
                                            <option value="SPB_2.3.1">SPB_2.3.1</option>
                                            <option value="SPB_2.3.0">SPB_2.3.0</option>
                                            <option value="SPB_2.2.9">SPB_2.2.9</option>
                                            <option value="SPB_2.2.0">SPB_2.2.0</option>
                                            <option value="SPB_2.1.8">SPB_2.1.8</option>
                                            <option value="SPB_2.0.0">SPB_2.0.0</option>
                                            <option value="SPB_1.7.9">SPB_1.7.9</option>
                                            <option value="SPB_1.6.8">SPB_1.6.8</option>
                                            <option value="SPB_1.5.6">SPB_1.5.6</option>
                                            <option value="V3.2.9">V3.2.9</option>
                                        </select>
                                    </div>

                                    <div className='grid grid-cols-2 items-center gap-3 mb-3'>
                                        <div className='flex gap-2 mt-2'>
                                            <label className='m-0 p-0'>
                                                <Checkbox
                                                    checked={motaEnabled}
                                                    onChange={(e) => setMotaEnabled(e.target.checked)}
                                                >
                                                    MOTA
                                                </Checkbox>
                                            </label>
                                        </div>
                                        <select
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            value={selectedMota}
                                            onChange={(e) => setSelectedMota(e.target.value)}
                                            disabled={!motaEnabled}
                                        >
                                            <option value="SPB9V_2.4.9">SPB9V_2.4.9</option>
                                            <option value="SPB_2.3.1">SPB_2.3.1</option>
                                            <option value="SPB_2.1.8">SPB_2.1.8</option>
                                            <option value="SPB_2.0.0">SPB_2.0.0</option>
                                            <option value="SPB_1.7.9">SPB_1.7.9</option>
                                            <option value="SPB_1.7.3">SPB_1.7.3</option>
                                            <option value="SPB_1.6.8">SPB_1.6.8</option>
                                        </select>
                                    </div>

                                    <label className='m-0 p-0 mb-3'>
                                        <Checkbox
                                            checked={mgmtEnabled}
                                            onChange={(e) => setMgmtEnabled(e.target.checked)}
                                        >
                                            mgmt=normal
                                        </Checkbox>
                                    </label>

                                    <label className='m-0 p-0 mb-3'>
                                        <Checkbox
                                            checked={diagEnabled}
                                            onChange={(e) => setDiagEnabled(e.target.checked)}
                                        >
                                            diag=reboot
                                        </Checkbox>
                                    </label>

                                    <div className='grid grid-cols-2 items-center gap-3 mb-3'>
                                        <div className='flex gap-2 mt-2'>
                                            <label className='m-0 p-0'>
                                                <Checkbox
                                                    checked={edrxEnabled}
                                                    onChange={(e) => setEdrxEnabled(e.target.checked)}
                                                >
                                                    Edrx
                                                </Checkbox>
                                            </label>
                                        </div>
                                        <select
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            value={selectedEdrx}
                                            onChange={(e) => setSelectedEdrx(e.target.value)}
                                            disabled={!edrxEnabled}
                                        >
                                            <option value="20">20</option>
                                            <option value="40">40</option>
                                            <option value="80">80</option>
                                        </select>
                                    </div>

                                    <div className='grid grid-cols-2 items-center gap-3 mb-3'>
                                        <div className='flex gap-2 mt-2'>
                                            <label className='m-0 p-0'>
                                                <Checkbox
                                                    checked={copsEnabled}
                                                    onChange={(e) => setCopsEnabled(e.target.checked)}
                                                >
                                                    cops
                                                </Checkbox>
                                            </label>
                                        </div>
                                        <select
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            value={selectedCops}
                                            onChange={(e) => setSelectedCops(e.target.value)}
                                            disabled={!copsEnabled}
                                        >
                                            <option value="att">🇺🇸 AT&T</option>
                                            <option value="verizon">🇺🇸 Verizon</option>
                                            <option value="tmobile">🇺🇸 T-Mobile</option>
                                            <option value="firstnet">🇺🇸 FirstNet (AT&T)</option>
                                            <option value="rogers">🇨🇦 Rogers Wireless</option>
                                            <option value="bell">🇨🇦 Bell Mobility</option>
                                            <option value="telus">🇨🇦 Telus Mobility</option>
                                            <option value="freedom">🇨🇦 Freedom Mobile</option>
                                        </select>
                                    </div>

                                    <div className='grid grid-cols-2 items-center gap-3 mb-3'>
                                        <div className='flex gap-2 mt-2'>
                                            <label className='m-0 p-0'>
                                                <Checkbox
                                                    checked={connModeEnabled}
                                                    onChange={(e) => setConnModeEnabled(e.target.checked)}
                                                >
                                                    connmode
                                                </Checkbox>
                                            </label>
                                        </div>
                                        <select
                                            className="w-full px-3 py-2 border rounded-md bg-white text-sm"
                                            value={selectedconnMode}
                                            onChange={(e) => setSelectedconnMode(e.target.value)}
                                            disabled={!connModeEnabled}
                                        >
                                            <option value="nbiot">nbiot</option>
                                            <option value="ltem">ltem</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Modal>}
        </div>
    );
};

export default ConnectionDashboard;